import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useGamesStore, useUserStore } from '../stores/StoreProvider';
import { Trophy, Plus, Calendar, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';


const Games = observer(() => {
  const gamesStore = useGamesStore();
  const userStore = useUserStore();

  useEffect(() => {
    gamesStore.fetchGames();
  }, []);

  const games = gamesStore.games;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Games</h1>
          <p className="text-gray-600">Track your hockey games and performance</p>
        </div>
        <Link
          to="/app/add-game"
          className="btn-primary"
        >
          <Plus className="mr-2" size={16} />
          Add Game
        </Link>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Trophy className="text-blue-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Games</p>
              <p className="text-2xl font-bold text-gray-900">{games.length}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-green-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Win Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {games.length > 0 
                  ? `${Math.round((games.filter(g => g.our_score > g.their_score).length / games.length) * 100)}%`
                  : '0%'
                }
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Calendar className="text-purple-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Season</p>
              <p className="text-2xl font-bold text-gray-900">
                {games.filter(g => {
                  const gameDate = new Date(g.date);
                  const now = new Date();
                  return gameDate.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Trophy className="text-orange-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Goals</p>
              <p className="text-2xl font-bold text-gray-900">
                {games.reduce((sum, game) => sum + (game.goals || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Games List */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Recent Games</h2>
        </div>
        <div className="divide-y divide-gray-200 px-15">
          {games.length > 0 ? (
            games.map((game, index) => (
              <div key={index} className="p-3 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  {/* Game Result Icon */}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                    game.our_score > game.their_score 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-red-100 text-red-600'
                  }`}>
                    <Trophy size={14} />
                  </div>
                  
                  {/* Teams and Score */}
                  <div className="flex items-center pw-50 max-w-[600px] justify-between flex-1">
                    {/* Our Team */}
                    <div className="flex items-center space-x-2">
                      {game.userOrganization?.image_url && (
                        <img
                          src={game.userOrganization.image_url}
                          alt="Team"
                          className="w-10 h-10 rounded object-contain"
                        />
                      )}
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-900">{game.our_score}</p>
                        <p className="text-xs text-gray-600  max-w-24">
                          {game.userOrganization?.team_name || game.userOrganization?.name || 'Your Team'}
                        </p>
                      </div>
                    </div>
                    
                    {/* VS */}
                    <div className="text-center px-4">
                      <p className="text-xs text-gray-400 font-medium">vs</p>
                      <p className="text-xs text-gray-400">
                        {new Date(game.date).toLocaleDateString()}
                      </p>
                    </div>
                    
                    {/* Opponent Team */}
                    <div className="flex items-center space-x-2">
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-900">{game.their_score}</p>
                        <p className="text-xs text-gray-600  max-w-24">
                          {game.opponent?.team_name || game.opponent?.name || 'Opponent'}
                        </p>
                      </div>
                      {game.opponent?.image_url && (
                        <img
                          src={game.opponent.image_url}
                          alt="Opponent"
                          className="w-10 h-10 rounded object-contain"
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="text-right ml-6">
                    <p className="text-sm font-medium text-gray-900">
                      {game.user?.position === 'goalie' ? (
                        `${game.saves || 0}SV, ${game.goals_against || 0}GA`
                      ) : (
                        `${game.goals}G, ${game.assists}A`
                      )}
                    </p>
                    <p className={`text-xs font-medium ${
                      game.our_score > game.their_score ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {game.our_score > game.their_score ? 'WIN' : 'LOSS'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-12 text-center">
              <Trophy className="mx-auto text-gray-400" size={48} />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No games yet</h3>
              <p className="mt-2 text-gray-500">Start tracking your games to see your progress</p>
              <Link
                to="/app/add-game"
                className="mt-4 btn-primary"
              >
                <Plus className="mr-2" size={16} />
                Add your first game
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default Games; 